import React, {
    ComponentProps,
    ComponentPropsWithRef,
    createContext,
    forwardRef,
    useContext,
    useState
} from 'react';
import styled from 'styled-components';
import { AnimatePresence, motion } from 'framer-motion';
import * as Accordion from '@radix-ui/react-accordion';
import { vars } from '../../theme';
import { inheritBorderRadiusColumn } from '../../styles/mixins';
import { MustIncludeOneOfBasicTestIdWithIdProps } from '../common/internal/test-id.types';
import { useBuildTestIdInternal } from '../../hooks/useBuildTestIdInternal';
import { useAccordionAnimation } from '../../hooks/useAccordionAnimation';

export type HBAccordionNewRootProps = ComponentPropsWithRef<
    typeof Accordion.Root
>;

export type HBAccordionNewItemProps = ComponentPropsWithRef<
    typeof Accordion.Item
>;

export type HBAccordionNewTriggerProps = ComponentProps<
    typeof Accordion.Trigger
> &
    MustIncludeOneOfBasicTestIdWithIdProps;

export type HBAccordionNewContentProps = ComponentPropsWithRef<
    typeof Accordion.Content
>;

const RootContext = createContext<{ value: string | string[] }>(null);

const ItemContext = createContext<{ isExpanded: boolean }>(null);

const Root: typeof Accordion.Root = forwardRef(
    (
        {
            children,
            defaultValue,
            value: externalValue,
            onValueChange: onExternalValueChange,
            ...props
        },
        ref
    ) => {
        const [internalValue, onInternalValueChange] = useState(defaultValue);

        const value = onExternalValueChange ? externalValue : internalValue;
        const onValueChange = onExternalValueChange || onInternalValueChange;

        return (
            <Accordion.Root
                ref={ref}
                value={value as any}
                onValueChange={onValueChange as any}
                {...props}
            >
                <RootContext.Provider value={{ value }}>
                    {children}
                </RootContext.Provider>
            </Accordion.Root>
        );
    }
);

const StyledItem = styled(Accordion.Item)`
    ${inheritBorderRadiusColumn}
`;

const Item: typeof Accordion.Item = forwardRef(
    ({ value: itemValue, children, ...props }, ref) => {
        const { value: rootValue } = useContext(RootContext);

        const isExpanded = Array.isArray(rootValue)
            ? rootValue.includes(itemValue)
            : rootValue === itemValue;

        return (
            <StyledItem value={itemValue} ref={ref} {...props}>
                <ItemContext.Provider value={{ isExpanded }}>
                    {children}
                </ItemContext.Provider>
            </StyledItem>
        );
    }
);

const Trigger = forwardRef<
    HTMLButtonElement,
    ComponentProps<typeof Accordion.Trigger> &
        MustIncludeOneOfBasicTestIdWithIdProps
>(({ children, id, testId, testIdName, ...props }, ref) => {
    const finalTestId = useBuildTestIdInternal({
        testId,
        identifier: testIdName || id,
        suffix: 'accordion-trigger',
        componentName: 'HBAccordionNew'
    });

    return (
        <StyledTrigger ref={ref} {...props} data-testid={finalTestId}>
            {children}
        </StyledTrigger>
    );
});

const Content: typeof Accordion.Content = forwardRef(
    ({ children, style, forceMount = true, className, ...props }, ref) => {
        const context = useContext(ItemContext);
        const animation = useAccordionAnimation(
            forceMount ? context : undefined
        );

        return (
            <AnimatePresence initial={false}>
                {(forceMount || context.isExpanded) && (
                    <MotionContent
                        ref={ref}
                        forceMount
                        {...(props as any)}
                        {...animation}
                    >
                        <div className={className} style={style}>
                            {children}
                        </div>
                    </MotionContent>
                )}
            </AnimatePresence>
        );
    }
);

// -------------------------- Styles -------------------------- //

const StyledTrigger = styled(Accordion.Trigger)`
    // reset angular styles
    padding: 0;
    margin: 0;
    width: 100%;
    text-align: initial;
    &,
    &:hover,
    &:focus,
    &:focus-visible {
        color: ${vars.colors.text.primary};
        background-color: unset;
    }
    &:focus-visible {
        outline: 2px solid ${vars.colors.stroke.keyboardFocus};
        outline-offset: 1px;
    }
`;

const MotionContent = motion(Accordion.Content);

export const HBAccordionNew = Object.assign(Root, {
    Root,
    Item,
    Trigger,
    Content
});

export default HBAccordionNew;
